import { type TObject, type TSchema, Type as t } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { Tag } from "./contract/primitives";
import { closed, literals, nullable } from "./schema-util";

/**
 * Every machine-readable error code in the system, with the HTTP status it travels under.
 *
 * Two families share one table:
 *   - API errors: returned as `{ error: { code, message, details? } }` with the status below.
 *   - Download failure reasons (`DOWNLOAD_ERROR_CODES`): stored on a download job as `errorCode`.
 * A few codes (POLICY_BLOCKED, GP_INSUFFICIENT) are both.
 */
export const ERROR_CODES = {
  // request shape / authentication / permission
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  INVALID_CREDENTIALS: 401,
  INVALID_SIGNATURE: 401,
  GP_INSUFFICIENT: 402,
  FORBIDDEN: 403,
  ACCOUNT_DISABLED: 403,
  // tag policy
  POLICY_BLOCKED: 403,
  POLICY_FIXED: 403,
  POLICY_ACK_REQUIRED: 409,
  // resources and state
  NOT_FOUND: 404,
  GALLERY_REMOVED: 410,
  CONFLICT: 409,
  VIDEO_TOO_LARGE: 413,
  DOWNLOAD_NOT_READY: 409,
  LEASE_INVALID: 409,
  RATE_LIMITED: 429,
  // server side
  INTERNAL: 500,
  NOT_IMPLEMENTED: 501,
  UNAVAILABLE: 503,
  // download failure reasons that are never an HTTP status of their own
  ARCHIVE_FORM_CHANGED: 502,
  ARCHIVE_SERVER_FAILED: 502,
  HITOMI_NOT_FOUND: 404,
  HITOMI_RUN_FAILED: 502,
  UPLOAD_MISMATCH: 502,
  EH_BLOCKED: 502,
  STALLED: 504,
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;
export const ERROR_CODE_LIST = Object.keys(ERROR_CODES) as ErrorCode[];

/** Reasons a download job can fail. Stored in downloads.error_code and reported by the Lambdas. */
export const DOWNLOAD_ERROR_CODES = [
  "GALLERY_REMOVED",
  "POLICY_BLOCKED",
  "GP_INSUFFICIENT",
  "ARCHIVE_FORM_CHANGED",
  "ARCHIVE_SERVER_FAILED",
  "HITOMI_NOT_FOUND",
  "HITOMI_RUN_FAILED",
  "UPLOAD_MISMATCH",
  "EH_BLOCKED",
  "STALLED",
  "INTERNAL",
] as const satisfies readonly ErrorCode[];
export type DownloadErrorCode = (typeof DOWNLOAD_ERROR_CODES)[number];

export function httpStatusOf(code: ErrorCode): number {
  return ERROR_CODES[code];
}

export const ErrorCodeSchema = literals(ERROR_CODE_LIST, { description: "Machine-readable error code" });
export const DownloadErrorCodeSchema = literals(DOWNLOAD_ERROR_CODES, {
  description: "Why a download job failed",
});

/**
 * What `error.details` may hold, per error code (U-15b; delta review D1).
 *
 * An error response is as closed as a success response. A code listed here may carry `details` of exactly
 * this shape (every object closed); a code NOT listed here carries no `details` at all. There is no free-form
 * record: a handler that throws an AppError with anything else in `details` (a database row, a gallery, a
 * thumbnail address) gets a 500 INTERNAL without details instead of having it serialised (`sealError`).
 * Adding a field or a code here is a contract change.
 */
export const ERROR_DETAILS = {
  /** where the request broke its schema (at most 10 entries) */
  VALIDATION_FAILED: closed({
    errors: t.Array(closed({ path: t.String(), message: t.String() }), { maxItems: 10 }),
  }),
  /** the names of the signature headers that are required */
  INVALID_SIGNATURE: closed({
    headers: t.Array(
      t.String({ pattern: "^(x-volgo-[a-z-]{1,40}|x-signature-ed25519|x-signature-timestamp)$" }),
      { maxItems: 4 },
    ),
  }),
  /** the gallery's own tags that caused the block: tags only, never gallery fields */
  POLICY_BLOCKED: closed({ matchedTags: t.Array(Tag) }),
  /** the gallery's own tags that need the user's confirmation */
  POLICY_ACK_REQUIRED: closed({ matchedTags: t.Array(Tag) }),
  /** internal.syncPage out of order: the cursor the run is at (null = newest page, or the run was stopped) */
  CONFLICT: closed({ expectedCursor: nullable(t.Integer()) }),
} as const satisfies Partial<Record<ErrorCode, TObject>>;

export type ErrorCodeWithDetails = keyof typeof ERROR_DETAILS;

/** The details schema of a code, or undefined when the code carries none. */
export function errorDetailsSchema(code: ErrorCode): TSchema | undefined {
  return (ERROR_DETAILS as Partial<Record<ErrorCode, TSchema>>)[code];
}

/** Whether `details` is allowed on an error with this code. `undefined` (no details) always is. */
export function errorDetailsAllowed(code: ErrorCode, details: unknown): boolean {
  if (details === undefined) return true;
  const schema = errorDetailsSchema(code);
  try {
    return schema !== undefined && Value.Check(schema, details);
  } catch {
    return false;
  }
}

export interface ErrorBodyType {
  error: { code: ErrorCode; message: string; details?: Record<string, unknown> };
}

/** The body of one code: closed, and `details` exists as a property only when the code declares it. */
function errorBodyOf(code: ErrorCode) {
  const details = errorDetailsSchema(code);
  return closed({
    error: closed({
      code: t.Literal(code),
      message: t.String(),
      ...(details ? { details: t.Optional(details) } : {}),
    }),
  });
}

/** Body of every non-2xx response: one closed variant per error code. */
export const ErrorBody = t.Unsafe<ErrorBodyType>(
  t.Union(
    ERROR_CODE_LIST.map((code) => errorBodyOf(code)),
    { $id: "ErrorBody" },
  ),
);

/** Thrown by handlers and mapped to an ErrorBody response by the API kit. */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, message?: string, details?: Record<string, unknown>) {
    super(message ?? code);
    this.name = "AppError";
    this.code = code;
    this.details = details;
  }

  get status(): number {
    return ERROR_CODES[this.code];
  }

  /** true when `details` is absent or exactly what ERROR_DETAILS declares for this code */
  get detailsAllowed(): boolean {
    return errorDetailsAllowed(this.code, this.details);
  }

  /**
   * A detached, validated JSON body. Cloning removes prototype serializers; checking both the source and
   * snapshot rejects undeclared source fields even when a serializer would hide them (D1/D2).
   */
  toBody(): ErrorBodyType {
    if (!this.detailsAllowed)
      throw new Error(`${this.code}: error details break the contract and are not serialised`);
    const body = structuredClone({
      error: { code: this.code, message: this.message, ...(this.details ? { details: this.details } : {}) },
    });
    if (!Value.Check(ErrorBody, body))
      throw new Error(`${this.code}: error body breaks the contract and is not serialised`);
    return body;
  }
}

/**
 * The body and status that may be sent to a client. Never serialize the original error again: the body is
 * a validated snapshot. Invalid details, throwing getters and clone failures become a bare INTERNAL.
 */
export function sealError(error: AppError): { body: ErrorBodyType; status: number } {
  try {
    const body = error.toBody();
    return { body, status: httpStatusOf(body.error.code) };
  } catch {
    return { body: { error: { code: "INTERNAL", message: "internal error" } }, status: 500 };
  }
}
